import { useCallback, useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

/**
 * Groupes d'engagement du joueur.
 *
 * Ils sont désormais rattachés au COMPTE (`player_engagement_group`, route
 * `/api/engagement-groups`) : on les retrouve d'un poste à l'autre. Le `localStorage`
 * reste utilisé comme **cache et repli** — si la migration n'est pas encore appliquée
 * (503) ou si le réseau tombe, la fonctionnalité continue de marcher en local.
 *
 * Reprise automatique : des groupes déjà présents en local et un compte encore vide
 * côté serveur ⇒ on pousse les groupes locaux une fois, sans rien demander au joueur.
 */

export interface EngagementGroup {
  id: string;
  nom: string;
  couleur: string;
  /** type d'engin → nombre demandé */
  compo: Record<string, number>;
}

const STORAGE_KEY = 'opsecours:engagementGroups';

export const GROUP_COLORS = ['#F01C1C', '#00B0F0', '#F7941D', '#6B3FA0', '#22B14C', '#EDE500'];

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function normalize(raw: unknown): EngagementGroup[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((g: unknown) => g && typeof g === 'object')
    .map((g: any) => ({
      id: String(g.id || ''),
      nom: String(g.nom || 'Groupe'),
      couleur: String(g.couleur || GROUP_COLORS[0]),
      compo:
        g.compo && typeof g.compo === 'object'
          ? Object.fromEntries(
              Object.entries(g.compo)
                .map(([k, v]) => [String(k), Math.max(0, Number(v) || 0)])
                .filter(([, v]) => Number(v) > 0),
            )
          : {},
    }))
    .filter((g: EngagementGroup) => g.id.length > 0);
}

function readLocal(): EngagementGroup[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? normalize(JSON.parse(raw)) : [];
  } catch {
    return [];
  }
}

function writeLocal(groups: EngagementGroup[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(groups));
  } catch {
    /* quota / navigation privée : on ignore */
  }
}

export function useEngagementGroups() {
  const [groups, setGroups] = useState<EngagementGroup[]>([]);
  /** false = serveur indisponible (migration non appliquée, hors ligne) → mode local seul. */
  const serverOkRef = useRef(true);
  const base = `${getApiBaseUrl(true)}/engagement-groups`;

  /** Pousse l'état courant au serveur ; bascule en local si le serveur refuse. */
  const push = useCallback(
    async (next: EngagementGroup[]) => {
      writeLocal(next);
      if (!serverOkRef.current) return;
      try {
        const res = await fetch(base, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ groups: next }),
        });
        if (!res.ok) {
          serverOkRef.current = false;
          return;
        }
        const data = await res.json();
        // Table pas encore créée (migration non appliquée) : on reste en local, sans écraser.
        if (data?.persisted === false) {
          serverOkRef.current = false;
          return;
        }
        if (data?.success && Array.isArray(data.groups)) {
          // Le serveur renvoie les ids définitifs : on s'aligne dessus.
          const fromServer = normalize(data.groups);
          setGroups(fromServer);
          writeLocal(fromServer);
        }
      } catch {
        serverOkRef.current = false;
      }
    },
    [base],
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      const local = readLocal();
      try {
        const res = await fetch(base, { headers: authHeaders() });
        if (!res.ok) throw new Error('indisponible');
        const data = await res.json();
        if (!alive) return;
        // Table absente : mode local, on garde les groupes existants sans tenter de PUT.
        if (data?.persisted === false) {
          serverOkRef.current = false;
          setGroups(local);
          return;
        }
        const fromServer = normalize(data?.groups);
        if (fromServer.length === 0 && local.length > 0) {
          // Compte vide côté serveur mais groupes existants en local : reprise une fois.
          setGroups(local);
          void push(local);
          return;
        }
        setGroups(fromServer);
        writeLocal(fromServer);
      } catch {
        if (!alive) return;
        serverOkRef.current = false;
        setGroups(local);
      }
    })();
    return () => {
      alive = false;
    };
  }, [base, push]);

  const saveGroup = useCallback(
    (g: EngagementGroup) => {
      setGroups((prev) => {
        const exists = prev.some((x) => x.id === g.id);
        const next = exists ? prev.map((x) => (x.id === g.id ? g : x)) : [...prev, g];
        void push(next);
        return next;
      });
    },
    [push],
  );

  const deleteGroup = useCallback(
    (id: string) => {
      setGroups((prev) => {
        const next = prev.filter((x) => x.id !== id);
        void push(next);
        return next;
      });
    },
    [push],
  );

  return { groups, saveGroup, deleteGroup };
}

export function newGroupId(): string {
  return `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}
